<script setup lang="ts">
import type { UserLimitTime, UserLimitPayload } from '@/types/UserLimits';

type Props = {
  hideSubmit?: boolean;
  isModal?: boolean;
  hideLabel?: boolean;
  // Minha Conta V2 (ScreenAccountProtection.vue) embute este componente
  // antigo, mas quer o botão "Salvar" na cor v2-primary (escura), não o azul
  // claro do site antigo.
  variant?: 'default' | 'v2';
};

const props = withDefaults(defineProps<Props>(), {
  isModal: false,
  hideLabel: false,
  variant: 'default',
});

const { $containment } = useNuxtApp();
const { t } = useTexts();
const { openToaster } = useLayoutStore();
const { getDateTime } = useDateTimeHelper();
const { setUserExceedTimeoutLimit } = useUserLimitsStore();
const { userLimits, setAccountLimits } = useUserLimits();

const {
  choiceOptions,
  periodsOptions,
  isBlockedToChange,
  getTimeToNextChange,
} = useAccountProtection();

const userLimitTime = ref<UserLimitTime>({
  active: 0,
  period: '0',
  amount: {
    month: 0,
    day: 0,
    hour: 0,
    minute: 0,
  },
});

const isLoading = ref(false);

const messageToAlertTime = computed(() => {
  if (!xsdDuration.value || !Number(userLimitTime.value.period)) {
    return t('account.account_protection.alert_select_time');
  }

  return `
    ${t('account.account_protection.max_access', { appName: appName() })}
    ${formatTimeMessage()}
    ${t('account.account_protection.during_period')}
    <strong>
      ${findPeriodLabel(userLimitTime.value.period)}
    </strong>
  `;
});

const spaLimitTimeFormatted = computed(() => {
  if (!userLimits.value?.spa_limit_time) {
    return;
  }

  return `${userLimits.value?.spa_limit_time} ${t('general.hours')}`;
});

const isTimeLimitChangeBlocked = computed(() => {
  return isBlockedToChange(userLimits.value?.user_limit_time_change);
});

const isWeeklyPeriod = computed(() => {
  return Number(userLimitTime.value.period) === 2;
});

function findPeriodLabel(period: string) {
  return periodsOptions.find((option: any) => {
    return option.value === period;
  })?.label;
}

function convertToXSDDuration(amount: {
  month?: number;
  day?: number;
  hour?: number;
  minute?: number;
}) {
  if (!amount.month && !amount.day && !amount.hour && !amount.minute) {
    return '';
  }

  const months = amount.month && amount.month > 0 ? `${amount.month}M` : '';
  const days = amount.day && amount.day > 0 ? `${amount.day}D` : '';
  const hours = amount.hour && amount.hour > 0 ? `${amount.hour}H` : '';
  const minutes = amount.minute && amount.minute > 0 ? `${amount.minute}M` : '';

  if (!months && !days && !hours && !minutes) {
    return '';
  }

  const timePart = hours || minutes ? `T${hours}${minutes}` : '';

  return `P${months}${days}${timePart}`;
}

const xsdDuration = computed(() => {
  return convertToXSDDuration(userLimitTime.value.amount);
});

function parseXSDDuration(duration: string): {
  month: number;
  day: number;
  hour: number;
  minute: number;
} {
  const regex = /P(?:(\d+)M)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?)?/;
  const matches = duration.match(regex);

  return {
    month: Number(matches?.[1] || 0),
    day: Number(matches?.[2] || 0),
    hour: Number(matches?.[3] || 0),
    minute: Number(matches?.[4] || 0),
  };
}

function getTimeLabel(amount?: number, unit?: string) {
  return amount && amount > 0
    ? t(`account.account_protection.${unit}`, { count: amount })
    : '';
}

function formatTimeMessage() {
  const monthLabel = getTimeLabel(
    userLimitTime.value.amount.month,
    userLimitTime.value.amount.month === 1 ? 'month' : 'months',
  );

  const dayLabel = getTimeLabel(
    userLimitTime.value.amount.day,
    userLimitTime.value.amount.day === 1 ? 'day' : 'days',
  );

  const hourLabel = getTimeLabel(
    userLimitTime.value.amount.hour,
    userLimitTime.value.amount.hour === 1 ? 'hour' : 'hours',
  );

  const minuteLabel = getTimeLabel(
    userLimitTime.value.amount.minute,
    userLimitTime.value.amount.minute === 1 ? 'minute' : 'minutes',
  );

  return `
    <strong>
      ${monthLabel}${monthLabel ? ',' : ''}
      ${dayLabel}${dayLabel ? ',' : ''}
      ${hourLabel}${hourLabel ? ',' : ''}
      ${minuteLabel}
    </strong>
  `;
}

function handlePeriodChange() {
  if (userLimitTime.value.period === '1') {
    userLimitTime.value.amount.month = 0;
    userLimitTime.value.amount.day = 0;
  } else if (
    userLimitTime.value.period === '2' ||
    userLimitTime.value.period === '3'
  ) {
    userLimitTime.value.amount.month = 0;
    userLimitTime.value.amount.day = 0;
  }
}

async function handleSubmit() {
  if (isLoading.value) {
    return;
  }

  if (isBlockedToChange(userLimits.value?.user_limit_time_change)) {
    return;
  }

  if (userLimitTime.value.active) {
    let message;

    if (!Number(userLimitTime.value.period)) {
      message = t('account.account_protection.select_period');
    } else if (!xsdDuration.value) {
      message = t('account.account_protection.type_amount');
    }

    if (message) {
      openToaster({
        message,
        type: 'error',
      });

      return;
    }
  }

  await $containment.handle('update_limits', () => {
    setUserExceedTimeoutLimit(false);
    sendSubmit();
  });
}

async function sendSubmit() {
  const { $kyc } = useNuxtApp();
  const { temporaryPassword } = useModulesValidation();

  isLoading.value = true;

  const payload: UserLimitPayload = {
    type: 'time',
  };

  if (Number(userLimitTime.value.active)) {
    payload.user_limit_time_active = Number(userLimitTime.value.active);
    payload.user_limit_time_period = Number(userLimitTime.value.period);
    payload.user_limit_time = xsdDuration.value;
  } else {
    payload.user_limit_time_active = 0;
  }

  if ($kyc?.current?.id) {
    payload.kyc_id = $kyc.current.id;
  }

  if (temporaryPassword.get()) {
    payload.password_check = temporaryPassword.get();
  }

  await setAccountLimits(payload)
    .then((res: any) => {
      const message =
        res.data?.message || t('account.account_protection.update_success');

      openToaster({
        message,
        type: 'success',
      });
    })
    .catch((error: any) => {
      const message =
        error.data?.message ||
        error.message ||
        t('account.account_protection.generic_error');

      openToaster({
        message,
        type: 'error',
      });
    })
    .finally(() => {
      isLoading.value = false;
      temporaryPassword.clear();
    });
}

const setUserLimits = () => {
  if (userLimits.value?.spa_limit_time) {
    userLimitTime.value.active = 1;
    userLimitTime.value.period = '4';
    userLimitTime.value.amount = parseXSDDuration(
      userLimits.value.spa_limit_time,
    );
  } else {
    if (userLimits.value?.user_limit_time_active) {
      userLimitTime.value.active = Number(
        userLimits.value?.user_limit_time_active,
      );
    }

    if (userLimits.value?.user_limit_time_period) {
      userLimitTime.value.period = String(
        userLimits.value?.user_limit_time_period,
      );
    }

    if (userLimits.value?.user_limit_time) {
      userLimitTime.value.amount = parseXSDDuration(
        userLimits.value?.user_limit_time,
      );
    }
  }
};

defineExpose({
  userLimitTime,
  xsdDuration,
  isTimeLimitChangeBlocked,
});

onMounted(() => {
  setUserLimits();
});
</script>

<template>
  <div :class="$style.timeLimit">
    <BaseLoading
      class="!bg-auth-bg-primary !absolute"
      :icon-loader="true"
      :show="isLoading"
    />

    <div :class="$style.groupRadio">
      <label v-if="!hideLabel">
        {{ $t('account.account_protection.set_limit') }}
      </label>

      <BaseFormRadio
        v-model="userLimitTime.active"
        :disabled="isTimeLimitChangeBlocked"
        :options="choiceOptions"
      />
    </div>

    <div
      v-if="!!userLimitTime.active"
      :class="[$style.groupFields, props.isModal && $style.groupFieldsModal]"
    >
      <div
        class="max-w-full md:max-w-52 min-w-48"
        :class="$style.groupInput"
      >
        <BaseFormSelectField
          id="user_limit_time_period"
          v-model="userLimitTime.period"
          :disabled="isTimeLimitChangeBlocked"
          :initial-value="userLimitTime.period"
          name="user_limit_time_period"
          :options="periodsOptions"
          @change="handlePeriodChange"
        />
      </div>

      <div
        class="flex-1 max-w-full"
        :class="$style.groupInput"
      >
        <div class="grid grid-cols-2 md:grid-cols-4 gap-2">
          <BaseFormInputNumber
            v-if="Number(userLimitTime.period) >= 4"
            id="userLimitTime"
            v-model="userLimitTime.amount.month"
            :disabled="isTimeLimitChangeBlocked"
            :initial-value="userLimitTime.amount.month"
            :label="$t('general.months')"
            :max="12"
            :min="0"
            name="userLimitTime"
            size="lg"
          />

          <BaseFormInputNumber
            v-if="Number(userLimitTime.period) >= 2"
            id="userLimitTime"
            v-model="userLimitTime.amount.day"
            :disabled="isTimeLimitChangeBlocked"
            :initial-value="userLimitTime.amount.day"
            :label="$t('general.days')"
            :max="isWeeklyPeriod ? 6 : 31"
            :min="0"
            name="userLimitTime"
            size="lg"
          />

          <BaseFormInputNumber
            v-if="Number(userLimitTime.period) >= 1"
            id="userLimitTime"
            v-model="userLimitTime.amount.hour"
            :disabled="isTimeLimitChangeBlocked"
            :initial-value="userLimitTime.amount.hour"
            :label="$t('general.hours')"
            :max="23"
            :min="0"
            name="userLimitTime"
            size="lg"
          />

          <BaseFormInputNumber
            v-if="Number(userLimitTime.period) >= 1"
            id="userLimitTime"
            v-model="userLimitTime.amount.minute"
            :disabled="isTimeLimitChangeBlocked"
            :initial-value="userLimitTime.amount.minute"
            :label="$t('general.minutes')"
            :max="59"
            :min="0"
            name="userLimitTime"
            size="lg"
          />
        </div>
      </div>
    </div>

    <div
      v-if="messageToAlertTime && !!userLimitTime.active"
      class="alert alert-info mt-4 text-xs"
      :class="{
        [$style.resetMarginTop]: isModal && userLimitTime.period === '0',
      }"
      v-html="messageToAlertTime"
    />

    <div
      v-if="
        userLimits?.user_limit_time_change &&
        isBlockedToChange(userLimits?.user_limit_time_change)
      "
      :class="$style.lastChange"
    >
      {{ $t('account.account_protection.last_change') }}
      <strong>
        {{ getDateTime(userLimits?.user_limit_time_change) }}
      </strong>
      | {{ $t('account.account_protection.next_change') }}
      <strong>
        {{ getTimeToNextChange(userLimits?.user_limit_time_change) }}
      </strong>
    </div>

    <button
      v-if="!hideSubmit"
      :class="[
        $style.btnSubmit,
        { [$style.btnSubmitV2]: props.variant === 'v2' },
      ]"
      :disabled="isBlockedToChange(userLimits?.user_limit_time_change)"
      @click="handleSubmit()"
    >
      <NuxtIcon name="check" />
      {{ $t('account.save') }}
    </button>

    <div
      v-if="!!spaLimitTimeFormatted"
      class="alert alert-warning"
      :class="$style.alertSpa"
    >
      <p
        v-html="
          $t('account.account_protection.spa_limit_alert', {
            limit: t('account.account_protection.user_limit_time'),
            value: spaLimitTimeFormatted,
          })
        "
      ></p>
    </div>
  </div>
</template>

<style module lang="postcss">
.timeLimit {
  @apply w-full;

  .groupRadio {
    @apply flex items-center gap-5 font-semibold text-xs md:text-sm text-texts;
  }

  .groupFields {
    @apply flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-5 mt-5;

    .groupInput {
      @apply w-full min-w-40 md:max-w-52;
    }
  }

  .groupFieldsModal {
    @apply flex flex-col gap-4 mt-5 items-start;

    .groupInput {
      @apply w-full min-w-0 max-w-full;
    }
  }

  .lastChange {
    @apply text-2xs text-warning/70 mt-4;
  }

  .alertSpa {
    @apply mt-4 text-xs;

    strong {
      &:first-child {
        @apply lowercase;
      }
    }
  }

  .btnSubmit {
    @apply bg-button-bg text-button-text py-2 px-4 rounded-md text-sm font-semibold whitespace-nowrap flex items-center justify-center gap-2 mt-5 disabled:opacity-40 disabled:cursor-not-allowed;

    /* mesmo botão do resto do Minha Conta V2 (Figma 19831:60693, botão
       "Resgatar recompensa": Plus Jakarta Sans Regular, não Semibold) */
    &.btnSubmitV2 {
      @apply rounded-[12px] bg-v2-primary font-normal text-v2-primary-foreground;
    }
  }

  .resetMarginTop {
    margin-top: 0 !important;
  }
}
</style>
